import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { CarListSort } from "@apac/components/cars";
import { useModuleBEM } from "@b2bportal/core-themes";
import { useDurationTracker } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  CarList,
  CarListError,
  CarListLoading,
  CarsListNoItems,
  type AvailabilityPage,
  getBaggageQty,
} from "@tenant/cars";
import { Page } from "@tenant/ui";
import clsx from "clsx";
import { CarAvailabilityCard } from "../../components/PackageCarAvailabilityCard";
import { PackageCarFilters } from "../../components/PackageCarFilters";
import defaultStyles from "./PackageCarsAvailabilityPage.module.scss";
import { PackageBreadcrumbs } from "@/overrides/packages";
import {
  PackageNoAvailabilityModal,
  PackagePriceSummary,
} from "@/pages/Packages/components";
import { CombinedPackageSteps } from "@/pages/Packages/hooks/usePackageFlow";
import { PackageBaseSelectors } from "@/store/packages/selectors";
import { useCarsStyles } from "~core-cars/themes";
import { CarsRawEventName, useEmitCoreCarsRawEvent } from "~core-cars/tracking";
import { CoreCarsComponent } from "~core-cars/types";

const COMPONENT_KEY = CoreCarsComponent.AvailabilityPage;

// NOTE: This is a direct copy of core/cars AvailabilityPage to more easily add package-specific components
export const PackageCarsAvailabilityPage = ({
  context: { isError = false, carsList, isLoading, hasFilters },
  handlers: { onErrorReload },
  children,
  ...pageProps
}: React.ComponentProps<typeof AvailabilityPage>) => {
  const styles = useCarsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t } = useI18nContext();

  const { className, ...restPageProps } = pageProps;

  const loadingTracker = useDurationTracker();
  useEffect(() => {
    if (isLoading) loadingTracker.start();
    if (!isLoading) loadingTracker.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const emitPageLoading = useEmitCoreCarsRawEvent(
    CarsRawEventName.ViewedCarAvailabilityPageLoading
  );

  const emitPageLoaded = useEmitCoreCarsRawEvent(
    CarsRawEventName.CarAvailabilityPageLoaded
  );

  useEffect(() => {
    if (!isLoading && !isError && carsList != null) {
      emitPageLoading({
        properties: {
          runTime: loadingTracker.duration ?? 0,
        },
      });
      emitPageLoaded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isError]);

  const benchmarkCar = useSelector(PackageBaseSelectors.getBenchmarkCar);

  const { benchmarkCarFromList, remainingCars } = useMemo(() => {
    if (!benchmarkCar || !carsList) {
      return { benchmarkCarFromList: null, remainingCars: carsList ?? [] };
    }

    const { vehicle } = benchmarkCar;
    const benchmarkIndex = carsList.findIndex((currentCar) => {
      const { vehicle: currentVehicle } = currentCar;
      const hasSameName = vehicle.name === currentVehicle.name;
      const hasSameAcrissCode =
        vehicle.acrissCode === currentVehicle.acrissCode;
      const hasSameImage =
        vehicle.images?.["original"] === currentVehicle.images?.["original"];
      const hasSamePassengerQty =
        vehicle.passengerQty === currentVehicle.passengerQty;
      const hasSameBaggageQty =
        getBaggageQty(vehicle) === getBaggageQty(currentVehicle);
      const hasSameFuelType = vehicle.fuelType === currentVehicle.fuelType;

      return (
        hasSameName &&
        hasSameAcrissCode &&
        hasSameImage &&
        hasSamePassengerQty &&
        hasSameBaggageQty &&
        hasSameFuelType
      );
    });

    if (benchmarkIndex === -1) {
      return { benchmarkCarFromList: null, remainingCars: carsList };
    }

    return {
      benchmarkCarFromList: carsList[benchmarkIndex],
      remainingCars: carsList.filter((_, index) => index !== benchmarkIndex),
    };
  }, [benchmarkCar, carsList]);

  return (
    <Page className={clsx(block, className)} {...restPageProps}>
      <div className={cn("container")}>
        <PackageBreadcrumbs
          currentStep={CombinedPackageSteps.CAR_AVAILABILITY}
          className={cn("breadcrumbs")}
        />
        <div className={cn("header-content")}>
          <h1 className={clsx(cn("title"), cn("left-content"))}>
            {t("core-packages.cars.carAvailabilityTitle")}
          </h1>
          <PackagePriceSummary />
        </div>
        <div className={cn("header-content")}>
          {carsList != null && carsList.length > 0 && (
            <div className={clsx(cn("car-list-details"), cn("left-content"))}>
              <p className={cn("cars-shown")}>
                {t("core-cars.availability.title", {
                  count: carsList.length,
                })}
              </p>

              <p className={cn("subtitle")}>
                {` | ${t("core-packages.cars.carAvailabilitySubtitle")}`}
              </p>
            </div>
          )}

          <CarListSort className={cn("sort-button")} />
        </div>
        <PackageCarFilters className={cn("filters")} />
        <div className={cn("list-container")}>
          {isError ? (
            <CarListError onErrorReload={onErrorReload} />
          ) : carsList != null && carsList.length > 0 ? (
            <>
              {benchmarkCarFromList && (
                <>
                  <div className={cn("benchmark-section")}>
                    <h5 className={cn("benchmark-header")}>
                      {t("core-packages.cars.includedInPackage")}
                    </h5>
                    <CarAvailabilityCard car={benchmarkCarFromList} />
                  </div>
                  {remainingCars.length > 0 && (
                    <div className={cn("remaining-cars-message")}>
                      <p className={cn("remaining-cars-title")}>
                        {t("core-packages.cars.additionalCarsAvailableTitle")}
                      </p>
                      <p className={cn("remaining-cars-subtitle")}>
                        {t(
                          "core-packages.cars.additionalCarsAvailableSubtitle"
                        )}
                      </p>
                    </div>
                  )}
                </>
              )}
              {remainingCars.length > 0 && <CarList list={remainingCars} />}
            </>
          ) : !isLoading ? (
            <>
              <CarsListNoItems hasFilters={hasFilters ?? false} />
              <PackageNoAvailabilityModal shouldShow={!hasFilters} />
            </>
          ) : null}
        </div>
        {children}

        <CarListLoading isLoading={isLoading} />
      </div>
    </Page>
  );
};
